import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PulseLoader from '@/components/PulseLoader';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { fetchSessionInfo } from '../../../liveSession/api';

type ReportTab = 'overview' | 'attendee' | 'avg-attendee' | 'quiz-score';
type StudentFilter = 'all' | 'best' | 'need-help';

interface ReportNavState {
  sessionTitle?: string;
  trainerName?: string;
  batchLabel?: string;
  durationMinutes?: number | string;
  plannedDurationMinutes?: number | null;
}

interface ParticipantRow {
  id: string;
  usn: string;
  name: string;
  attended: boolean;
  attendedTimeMinutes?: number;
  attendancePercent?: number;
  /** Only set when API provides a real quiz score. */
  quizScore?: number;
  confidence?: number;
  engagementPct?: number;
  understanding?: 'understood' | 'partial' | 'not_understood';
  doubtsCount?: number;
  chatMessages?: number;
  avatarUrl?: string;
}

interface TopicTimeRow {
  title: string;
  plannedMinutes: number;
  actualMinutes: number;
}

interface TopicConfusionRow {
  topicTitle: string;
  confusionPercent: number;
  sessionMinute?: number;
}

interface QuizPushRow {
  id: string;
  label: string;
  status: string;
  submitted: number;
  avgScore: number;
}

interface UnderstandingSummary {
  understoodFully: number;
  partiallyUnderstood: number;
  didNotUnderstand: number;
  totalVotes: number;
}

interface EngagementOutreach {
  doubtsCount: number;
  chatOutreachCount: number;
  studentsWithDoubts: number;
  studentsWithChat: number;
}

interface FeedbackSummary {
  avgRating: number | null;
  ratingCount: number;
  reviews: Array<{
    student_id?: string;
    student_name?: string;
    rating: number;
    review?: string;
    submitted_at?: string | null;
  }>;
}

function formatFeedbackSubmittedAt(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

function normalizeFeedbackReviews(
  rawReviews: unknown[],
  nameByStudentId: Map<string, string>,
): FeedbackSummary['reviews'] {
  const rows: FeedbackSummary['reviews'] = [];
  for (const item of rawReviews) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const rating = pickOptionalNumber(row.rating);
    if (rating == null || rating < 1 || rating > 5) continue;
    const studentId = String(row.student_id || row.studentId || '').trim();
    const fromApi = String(row.student_name || row.studentName || '').trim();
    const studentName =
      fromApi || (studentId ? nameByStudentId.get(studentId) || '' : '') || undefined;
    const review = String(row.review || row.comment || '').trim();
    const submittedAt = String(
      row.submitted_at || row.submittedAt || row.created_at || row.createdAt || '',
    ).trim();
    rows.push({
      student_id: studentId || undefined,
      student_name: studentName,
      rating,
      review: review || undefined,
      submitted_at: submittedAt || null,
    });
  }
  rows.sort((a, b) => {
    const ta = a.submitted_at ? Date.parse(a.submitted_at) : 0;
    const tb = b.submitted_at ? Date.parse(b.submitted_at) : 0;
    return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
  });
  return rows;
}

function understandingLabel(level?: ParticipantRow['understanding']): string {
  if (level === 'understood') return 'Fully';
  if (level === 'partial') return 'Partial';
  if (level === 'not_understood') return 'Did not';
  return '—';
}

function attendeeToExcelRow(a: ParticipantRow, index: number) {
  return {
    Sn: index + 1,
    'Student ID': a.id,
    USN: a.usn,
    Student: a.name,
    Attended: a.attended ? 'Yes' : 'No',
    Understanding: understandingLabel(a.understanding),
    'Engagement %': a.engagementPct ?? '',
    Doubts: a.doubtsCount ?? 0,
    'Chat Messages': a.chatMessages ?? 0,
    'Quiz Score %': a.quizScore ?? '',
    'Confidence %': a.confidence ?? '',
  };
}

function pickStudentAvatarUrl(participant: Record<string, unknown>): string | undefined {
  const candidates = [
    participant.avatarUrl,
    participant.avatar_url,
    participant.profile_pic,
    participant.profilePic,
    participant.photo,
    participant.photo_url,
    participant.image,
  ];
  for (const value of candidates) {
    const url = String(value || '').trim();
    if (url && /^https?:\/\//i.test(url)) return url;
  }
  return undefined;
}

async function downloadElementAsPdf(el: HTMLElement, fileName: string): Promise<void> {
  const canvas = await html2canvas(el, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false,
    scrollX: 0,
    scrollY: 0,
    windowWidth: el.scrollWidth,
    windowHeight: el.scrollHeight,
    onclone: (_doc: Document, cloned: HTMLElement) => {
      cloned.querySelectorAll('[data-pdf-hide]').forEach((node: Element) => {
        (node as HTMLElement).style.display = 'none';
      });
    },
  });

  if (!canvas.width || !canvas.height) {
    throw new Error('Failed to render report for PDF');
  }

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 8;
  const usableWidth = pageWidth - margin * 2;
  const usableHeight = pageHeight - margin * 2;
  const imgWidthMm = usableWidth;
  const pxPerMm = canvas.width / imgWidthMm;
  const pageSlicePx = Math.max(1, Math.floor(usableHeight * pxPerMm));

  let srcY = 0;
  let pageIndex = 0;
  while (srcY < canvas.height - 1) {
    if (pageIndex > 0) pdf.addPage();
    const sliceHeightPx = Math.min(pageSlicePx, canvas.height - srcY);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = Math.ceil(sliceHeightPx);
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) throw new Error('Failed to create PDF page');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    ctx.drawImage(
      canvas,
      0,
      srcY,
      canvas.width,
      sliceHeightPx,
      0,
      0,
      canvas.width,
      sliceHeightPx,
    );
    const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.92);
    const sliceHeightMm = (sliceCanvas.height * imgWidthMm) / sliceCanvas.width;
    pdf.addImage(sliceData, 'JPEG', margin, margin, imgWidthMm, sliceHeightMm, undefined, 'FAST');
    srcY += sliceHeightPx;
    pageIndex += 1;
    if (pageIndex > 40) break;
  }

  pdf.save(fileName);
}

function pickOptionalNumber(...values: unknown[]): number | undefined {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function StudentAvatar({ src, name }: { src?: string; name: string }) {
  return (
    <Avatar
      src={src || undefined}
      alt={name}
      sx={{ width: 40, height: 40, bgcolor: '#e2e8f0', color: '#64748b' }}
    >
      <PersonOutlineIcon sx={{ fontSize: 22 }} />
    </Avatar>
  );
}

function EmptyCard({ title, message }: { title?: string; message: string }) {
  return (
    <Box
      sx={{
        borderRadius: '16px',
        border: '1px dashed #cbd5e1',
        backgroundColor: '#fff',
        p: 3,
        textAlign: 'center',
      }}
    >
      {title ? (
        <Typography sx={{ mb: 0.5, fontWeight: 700, color: '#0f172a', fontSize: 15 }}>
          {title}
        </Typography>
      ) : null}
      <Typography sx={{ fontSize: 14, color: '#64748b' }}>{message}</Typography>
    </Box>
  );
}

function matchesStudentFilter(
  quizScore: number | undefined,
  filter: StudentFilter,
): boolean {
  if (filter === 'all') return true;
  if (quizScore == null || !Number.isFinite(quizScore)) return false;
  if (filter === 'best') return quizScore >= 80;
  if (filter === 'need-help') return quizScore < 65;
  return true;
}

function StudentFilterToggle({
  value,
  onChange,
  bestCount,
  needHelpCount,
  quizDataAvailable,
}: {
  value: StudentFilter;
  onChange: (f: StudentFilter) => void;
  bestCount: number;
  needHelpCount: number;
  quizDataAvailable: boolean;
}) {
  const segments: { id: StudentFilter; label: string; activeBg: string; disabled?: boolean }[] = [
    {
      id: 'need-help',
      label: `Need help (${needHelpCount})`,
      activeBg: '#dc2626',
      disabled: !quizDataAvailable,
    },
    {
      id: 'best',
      label: `Best (${bestCount})`,
      activeBg: '#059669',
      disabled: !quizDataAvailable,
    },
    { id: 'all', label: 'All students', activeBg: '#1e293b' },
  ];

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        borderRadius: '999px',
        border: '1px solid #e2e8f0',
        backgroundColor: '#fff',
        px: 0.5,
        py: 0.5,
      }}
    >
      {segments.map(({ id, label, activeBg, disabled }) => {
        const active = value === id;
        return (
          <Button
            key={id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(id)}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              px: 2,
              py: 0.75,
              minWidth: 0,
              fontWeight: 600,
              fontSize: 13,
              color: active ? '#fff' : '#0f172a',
              backgroundColor: active ? activeBg : 'transparent',
              boxShadow: 'none',
              opacity: disabled ? 0.45 : 1,
              '&:hover': {
                backgroundColor: active ? activeBg : '#f1f5f9',
                boxShadow: 'none',
              },
            }}
          >
            {label}
          </Button>
        );
      })}
    </Box>
  );
}

function AttendeeTable({
  rows,
  showQuiz,
  showMetrics,
}: {
  rows: ParticipantRow[];
  showQuiz: boolean;
  showMetrics?: boolean;
}) {
  const colSpan = 3 + (showQuiz ? 2 : 0) + (showMetrics ? 3 : 0);
  return (
    <Box
      sx={{
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        backgroundColor: '#fff',
        overflow: 'hidden',
      }}
    >
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
        <Box
          component="thead"
          sx={{ backgroundColor: '#f8fafc', fontSize: 12, textTransform: 'uppercase', color: '#64748b' }}
        >
          <Box component="tr">
            <Box component="th" sx={{ px: 2, py: 1.5, fontWeight: 700 }}>Student ID</Box>
            <Box component="th" sx={{ px: 2, py: 1.5, fontWeight: 700 }}>Student</Box>
            <Box component="th" sx={{ px: 2, py: 1.5, fontWeight: 700 }}>Attended</Box>
            {showMetrics && (
              <>
                <Box component="th" sx={{ px: 2, py: 1.5, fontWeight: 700 }}>Understanding</Box>
                <Box component="th" sx={{ px: 2, py: 1.5, fontWeight: 700 }}>Engagement</Box>
                <Box component="th" sx={{ px: 2, py: 1.5, fontWeight: 700 }}>Doubts / Chat</Box>
              </>
            )}
            {showQuiz && (
              <>
                <Box component="th" sx={{ px: 2, py: 1.5, fontWeight: 700 }}>Quiz score</Box>
                <Box component="th" sx={{ px: 2, py: 1.5, fontWeight: 700 }}>Confidence</Box>
              </>
            )}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.length === 0 ? (
            <Box component="tr">
              <Box
                component="td"
                colSpan={colSpan}
                sx={{ px: 2, py: 4, textAlign: 'center', color: '#64748b', fontSize: 14 }}
              >
                No students to show.
              </Box>
            </Box>
          ) : (
            rows.map((a) => (
              <Box component="tr" key={a.id} sx={{ borderTop: '1px solid #f1f5f9' }}>
                <Box component="td" sx={{ px: 2, py: 1.5, fontSize: 13, color: '#475569', fontFamily: 'monospace' }}>
                  {a.id}
                </Box>
                <Box component="td" sx={{ px: 2, py: 1.5, fontWeight: 600, color: '#0f172a', fontSize: 14 }}>
                  {a.name}
                </Box>
                <Box component="td" sx={{ px: 2, py: 1.5, fontSize: 14, color: '#334155' }}>
                  {a.attended ? 'Yes' : 'No'}
                </Box>
                {showMetrics && (
                  <>
                    <Box component="td" sx={{ px: 2, py: 1.5, fontSize: 14, color: '#334155' }}>
                      {understandingLabel(a.understanding)}
                    </Box>
                    <Box component="td" sx={{ px: 2, py: 1.5, fontSize: 14, color: '#334155' }}>
                      {a.engagementPct != null ? `${a.engagementPct}%` : '—'}
                    </Box>
                    <Box component="td" sx={{ px: 2, py: 1.5, fontSize: 14, color: '#334155' }}>
                      {`${a.doubtsCount ?? 0} / ${a.chatMessages ?? 0}`}
                    </Box>
                  </>
                )}
                {showQuiz && (
                  <>
                    <Box component="td" sx={{ px: 2, py: 1.5, fontWeight: 700, color: '#059669', fontSize: 14 }}>
                      {a.quizScore != null ? `${a.quizScore}%` : '—'}
                    </Box>
                    <Box component="td" sx={{ px: 2, py: 1.5, fontSize: 14, color: '#475569' }}>
                      {a.confidence != null ? `${a.confidence}%` : '—'}
                    </Box>
                  </>
                )}
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
}

const cardSx = {
  borderRadius: '16px',
  border: '1px solid #e2e8f0',
  backgroundColor: '#fff',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  p: 2.5,
};

const PostSessionTrainerReport: React.FC = () => {
  const { sessionName } = useParams<{ sessionName: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navState = (location.state || {}) as ReportNavState;
  const sessionId = searchParams.get('sessionId') || '';

  const [tab, setTab] = useState<ReportTab>('overview');
  const [studentFilter, setStudentFilter] = useState<StudentFilter>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attendees, setAttendees] = useState<ParticipantRow[]>([]);
  const [sessionDuration, setSessionDuration] = useState<number | null>(
    pickOptionalNumber(navState.durationMinutes) ?? null,
  );
  const [plannedDuration, setPlannedDuration] = useState<number | null>(
    pickOptionalNumber(navState.plannedDurationMinutes) ?? null,
  );
  const [avgAttention, setAvgAttention] = useState<number | null>(null);
  const [attentionDelta, setAttentionDelta] = useState<number | null>(null);
  const [avgPreQuiz, setAvgPreQuiz] = useState<number | null>(null);
  const [avgPostQuiz, setAvgPostQuiz] = useState<number | null>(null);
  const [preQuizDelta, setPreQuizDelta] = useState<number | null>(null);
  const [postQuizDelta, setPostQuizDelta] = useState<number | null>(null);
  const [topicTimes, setTopicTimes] = useState<TopicTimeRow[]>([]);
  const [topicConfusion, setTopicConfusion] = useState<TopicConfusionRow[]>([]);
  const [quizPushes, setQuizPushes] = useState<QuizPushRow[]>([]);
  const [understanding, setUnderstanding] = useState<UnderstandingSummary | null>(null);
  const [engagementOutreach, setEngagementOutreach] = useState<EngagementOutreach | null>(null);
  const [feedback, setFeedback] = useState<FeedbackSummary | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [instructorName, setInstructorName] = useState<string>(navState.trainerName || '');
  const [batchFromApi, setBatchFromApi] = useState<string>('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const sessionTitle =
    navState.sessionTitle ||
    (sessionName || 'Session').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const trainerName =
    instructorName ||
    navState.trainerName ||
    '';
  const batchLabel =
    navState.batchLabel ||
    batchFromApi ||
    sessionStorage.getItem('meet_batch_name') ||
    '';

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!sessionId) {
        setError('Missing session id.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await fetchSessionInfo(sessionId);
        if (cancelled) return;

        if (data?.error) {
          setError(String(data.error));
          setAttendees([]);
          return;
        }

        const sessionInfoRaw = data?.sessionInfo;
        const sessionInfo = Array.isArray(sessionInfoRaw)
          ? sessionInfoRaw[0]
          : sessionInfoRaw;

        const duration = pickOptionalNumber(sessionInfo?.session_duration);
        if (duration != null) setSessionDuration(Math.round(duration));

        const planned = pickOptionalNumber(
          sessionInfo?.planned_duration,
          sessionInfo?.plannedDurationMinutes,
          navState.plannedDurationMinutes,
        );
        if (planned != null) setPlannedDuration(Math.round(planned));

        const inst = String(sessionInfo?.inst_name || sessionInfo?.instructor_name || '').trim();
        if (inst) setInstructorName(inst);

        setAvgAttention(pickOptionalNumber(sessionInfo?.avg_attention, sessionInfo?.avgAttention) ?? null);
        setAttentionDelta(pickOptionalNumber(sessionInfo?.attention_delta, sessionInfo?.attentionDelta) ?? null);
        setAvgPreQuiz(pickOptionalNumber(sessionInfo?.avg_pre_quiz, sessionInfo?.avgPreQuizScore) ?? null);
        setAvgPostQuiz(pickOptionalNumber(sessionInfo?.avg_post_quiz, sessionInfo?.avgPostQuizScore) ?? null);
        setPreQuizDelta(pickOptionalNumber(sessionInfo?.pre_quiz_delta, sessionInfo?.preQuizScoreDelta) ?? null);
        setPostQuizDelta(pickOptionalNumber(sessionInfo?.post_quiz_delta, sessionInfo?.postQuizScoreDelta) ?? null);

        const topics = (sessionInfo?.topic_times || sessionInfo?.topicTimes || data?.topicTimes || []) as TopicTimeRow[];
        setTopicTimes(Array.isArray(topics) ? topics.filter((t) => t?.title) : []);

        const confusion = (sessionInfo?.topic_confusion || sessionInfo?.topicConfusion || data?.topicConfusion || []) as TopicConfusionRow[];
        setTopicConfusion(Array.isArray(confusion) ? confusion.filter((t) => t?.topicTitle) : []);

        const pushes = (sessionInfo?.quiz_pushes || sessionInfo?.quizPushes || data?.quizPushes || []) as QuizPushRow[];
        setQuizPushes(Array.isArray(pushes) ? pushes : []);

        const undRaw = sessionInfo?.understanding || data?.understanding;
        if (undRaw && typeof undRaw === 'object') {
          setUnderstanding({
            understoodFully: pickOptionalNumber(undRaw.understood_fully, undRaw.understoodFully) ?? 0,
            partiallyUnderstood:
              pickOptionalNumber(undRaw.partially_understood, undRaw.partiallyUnderstood) ?? 0,
            didNotUnderstand:
              pickOptionalNumber(undRaw.did_not_understand, undRaw.didNotUnderstand) ?? 0,
            totalVotes: pickOptionalNumber(undRaw.total_votes, undRaw.totalVotes) ?? 0,
          });
        } else {
          setUnderstanding(null);
        }

        const engRaw = sessionInfo?.engagement_outreach || sessionInfo?.engagementOutreach;
        if (engRaw && typeof engRaw === 'object') {
          setEngagementOutreach({
            doubtsCount: pickOptionalNumber(engRaw.doubts_count, engRaw.doubtsCount) ?? 0,
            chatOutreachCount:
              pickOptionalNumber(engRaw.chat_outreach_count, engRaw.chatOutreachCount) ?? 0,
            studentsWithDoubts:
              pickOptionalNumber(engRaw.students_with_doubts, engRaw.studentsWithDoubts) ?? 0,
            studentsWithChat:
              pickOptionalNumber(engRaw.students_with_chat, engRaw.studentsWithChat) ?? 0,
          });
        } else {
          setEngagementOutreach(null);
        }

        const participants = (data?.participantInfo || []) as Array<Record<string, unknown>>;
        const nameByStudentId = new Map<string, string>();
        for (const p of participants) {
          const sid = String(p.student_id || '').trim();
          const name = String(p.display_name || p.email || '').trim();
          if (sid && name) nameByStudentId.set(sid, name);
        }

        const fbRaw = sessionInfo?.feedback || data?.feedback;
        if (fbRaw && typeof fbRaw === 'object') {
          const reviews = normalizeFeedbackReviews(
            Array.isArray(fbRaw.reviews) ? fbRaw.reviews : [],
            nameByStudentId,
          );
          const ratingCount =
            pickOptionalNumber(fbRaw.rating_count, fbRaw.ratingCount) ?? reviews.length;
          const avgRating =
            pickOptionalNumber(fbRaw.avg_rating, fbRaw.avgRating) ??
            (reviews.length
              ? Math.round(
                  (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length) * 10,
                ) / 10
              : null);
          setFeedback({
            avgRating,
            ratingCount: Math.max(ratingCount, reviews.length),
            reviews,
          });
        } else {
          setFeedback(null);
        }

        const durationForPct = duration ?? pickOptionalNumber(navState.durationMinutes) ?? 0;

        const rows: ParticipantRow[] = participants.map((p, i) => {
          const attendedTime = pickOptionalNumber(p.attendedTime, p.session_duration) ?? 0;
          const engagementPct = pickOptionalNumber(p.engagement_pct, p.engagementPct);
          // Backend backfills attendedTime from engagement when session_logs are empty.
          const attended =
            attendedTime > 0 ||
            Boolean(p.present) ||
            (engagementPct != null && engagementPct > 0);
          const attendancePercent =
            pickOptionalNumber(p.attendance_percentage, p.attendancePercent) ??
            (durationForPct > 0 && attendedTime > 0
              ? Math.round((attendedTime / durationForPct) * 100)
              : undefined);
          const quizScore = pickOptionalNumber(p.quiz_score, p.quizScore, p.score);
          const confidence = pickOptionalNumber(p.confidence, p.confidence_score);
          const rawLevel = String(p.understanding || '').toLowerCase();
          const understandingLevel =
            rawLevel === 'understood' || rawLevel === 'partial' || rawLevel === 'not_understood'
              ? (rawLevel as ParticipantRow['understanding'])
              : undefined;

          return {
            id: String(p.student_id || `student-${i}`),
            usn: String(p.usn ?? '').trim(),
            name: String(p.display_name || p.email || `Student ${i + 1}`),
            attended,
            attendedTimeMinutes: attendedTime > 0 ? attendedTime : undefined,
            attendancePercent,
            quizScore: quizScore != null ? Math.round(quizScore) : undefined,
            confidence: confidence != null ? Math.round(confidence) : undefined,
            engagementPct: engagementPct != null ? Math.round(engagementPct) : undefined,
            understanding: understandingLevel,
            doubtsCount: pickOptionalNumber(p.doubts_count, p.doubtsCount) ?? 0,
            chatMessages: pickOptionalNumber(p.chat_messages, p.chatMessages) ?? 0,
            avatarUrl: pickStudentAvatarUrl(p),
          };
        });

        const firstBatch = participants.find((p) => p.batch_name)?.batch_name;
        if (firstBatch) setBatchFromApi(String(firstBatch));

        setAttendees(rows);
      } catch (e) {
        console.error('Failed to load session report:', e);
        if (!cancelled) {
          setError('Could not load session report data.');
          setAttendees([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [sessionId, navState.plannedDurationMinutes, navState.durationMinutes]);

  const quizDataAvailable = attendees.some((a) => a.quizScore != null);
  const bestCount = attendees.filter((a) => a.quizScore != null && a.quizScore >= 80).length;
  const needHelpCount = attendees.filter((a) => a.quizScore != null && a.quizScore < 65).length;
  const attendanceCount = attendees.filter((a) => a.attended).length;
  const totalStudents = attendees.length;
  const attendancePercent =
    totalStudents > 0 ? Math.round((attendanceCount / totalStudents) * 100) : 0;

  useEffect(() => {
    if (!quizDataAvailable && studentFilter !== 'all') {
      setStudentFilter('all');
    }
  }, [quizDataAvailable, studentFilter]);

  const filteredAttendees = useMemo(
    () => attendees.filter((a) => matchesStudentFilter(a.quizScore, studentFilter)),
    [attendees, studentFilter],
  );

  const followUpStudents = useMemo(
    () =>
      attendees
        .filter((a) => a.quizScore != null && a.quizScore < 65)
        .map((a) => ({
          ...a,
          flags: [`Quiz Score: ${a.quizScore}%`],
        })),
    [attendees],
  );

  const filteredFollowUp = followUpStudents.filter((s) =>
    matchesStudentFilter(s.quizScore, studentFilter),
  );

  const sortedConfusion = [...topicConfusion].sort(
    (a, b) => b.confusionPercent - a.confusionPercent,
  );
  const maxTopicConfusion = Math.max(...sortedConfusion.map((t) => t.confusionPercent), 1);
  const peakTopic = sortedConfusion[0];

  const durationLabel =
    sessionDuration != null
      ? plannedDuration != null
        ? `Duration: ${sessionDuration} mins (planned ${plannedDuration}m)`
        : `Duration: ${sessionDuration} mins`
      : plannedDuration != null
        ? `Planned duration: ${plannedDuration}m`
        : null;

  const metaParts = [
    batchLabel ? `Batch: ${batchLabel}` : null,
    trainerName ? `Trainer: ${trainerName}` : null,
    durationLabel,
  ].filter(Boolean);

  const safeReportFileName = useMemo(
    () =>
      String(sessionTitle || 'session')
        .replace(/[^\w\-]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 60) || 'session',
    [sessionTitle],
  );

  const handleDownloadPdf = useCallback(async () => {
    const el = reportRef.current;
    if (!el || downloadingPdf) return;
    setDownloadingPdf(true);
    setError(null);
    try {
      await downloadElementAsPdf(el, `${safeReportFileName}_report.pdf`);
    } catch (err) {
      console.error('Session report PDF download failed', err);
      setError('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  }, [downloadingPdf, safeReportFileName]);

  const handleExportExcel = useCallback(() => {
    if (filteredAttendees.length === 0) return;
    const sheet = XLSX.utils.json_to_sheet(
      filteredAttendees.map((a, i) => attendeeToExcelRow(a, i)),
    );
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Attendees');
    XLSX.writeFile(book, `${safeReportFileName}_report.xlsx`);
  }, [filteredAttendees, safeReportFileName]);

  if (loading) {
    return (
      <Box
        sx={{
          height: 'calc(100vh - 90px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8fafc',
          borderRadius: 2,
          m: 1,
          mr: 2,
        }}
      >
        <PulseLoader color="#2563eb" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: 'calc(100vh - 90px)',
        overflow: 'auto',
        backgroundColor: '#f8fafc',
        border: '1px solid #e5e7eb',
        borderRadius: 2,
        my: 1,
        mr: 2,
        px: 2.5,
        py: 2,
      }}
    >
      <Box ref={reportRef}>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { lg: 'flex-start' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 26, color: '#0f172a', lineHeight: 1.25 }}>
            {sessionTitle}
          </Typography>
          {metaParts.length > 0 ? (
            <Typography sx={{ mt: 0.75, fontSize: 14, color: '#64748b' }}>
              {metaParts.join(' · ')}
            </Typography>
          ) : null}
          {error ? (
            <Typography sx={{ mt: 0.5, fontSize: 12, color: '#b45309' }}>{error}</Typography>
          ) : null}
        </Box>
        <Box
          data-pdf-hide
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={attendees.length === 0}
            onClick={handleExportExcel}
            sx={{
              textTransform: 'none',
              borderRadius: '12px',
              fontWeight: 700,
              borderColor: '#cbd5e1',
              color: '#334155',
              px: 2,
              py: 1,
              boxShadow: 'none',
              '&:hover': { borderColor: '#94a3b8', backgroundColor: '#f8fafc', boxShadow: 'none' },
            }}
          >
            Export Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={downloadingPdf}
            onClick={() => void handleDownloadPdf()}
            sx={{
              textTransform: 'none',
              borderRadius: '12px',
              fontWeight: 700,
              backgroundColor: '#2563eb',
              px: 2,
              py: 1,
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#1d4ed8', boxShadow: 'none' },
            }}
          >
            {downloadingPdf ? 'Preparing PDF…' : 'Download PDF'}
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {(
          [
            ['overview', 'Overview'],
            ['attendee', 'Attendee'],
            ['avg-attendee', 'Avg Attendee'],
            ['quiz-score', 'Quiz Score'],
          ] as const
        ).map(([id, label]) => {
          const active = tab === id;
          return (
            <Button
              key={id}
              onClick={() => setTab(id)}
              sx={{
                textTransform: 'none',
                borderRadius: '999px',
                px: 2,
                py: 1,
                fontWeight: 600,
                fontSize: 14,
                color: active ? '#fff' : '#475569',
                backgroundColor: active ? '#2563eb' : '#fff',
                border: active ? 'none' : '1px solid #e2e8f0',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: active ? '#1d4ed8' : '#f8fafc',
                  boxShadow: 'none',
                },
              }}
            >
              {label}
            </Button>
          );
        })}
      </Box>

      {tab === 'overview' && (
        <>
          <Box sx={{ mb: 3 }}>
            <StudentFilterToggle
              value={studentFilter}
              onChange={setStudentFilter}
              bestCount={bestCount}
              needHelpCount={needHelpCount}
              quizDataAvailable={quizDataAvailable}
            />
          </Box>

          <Box
            sx={{
              mb: 3,
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(5, minmax(0, 1fr))',
              },
            }}
          >
            <Box sx={cardSx}>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <PeopleAltOutlinedIcon sx={{ color: '#2563eb' }} />
                {totalStudents > 0 ? (
                  <Box
                    sx={{
                      borderRadius: '999px',
                      backgroundColor: '#d1fae5',
                      color: '#047857',
                      px: 1,
                      py: 0.25,
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    {attendancePercent}%
                  </Box>
                ) : null}
              </Box>
              <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>
                {totalStudents > 0 ? `${attendanceCount}/${totalStudents}` : '—'}
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 14, color: '#64748b' }}>Attendance</Typography>
            </Box>

            <Box sx={cardSx}>
              <Typography sx={{ mb: 1, fontSize: 12, fontWeight: 700, color: '#64748b' }}>
                Understanding
              </Typography>
              {understanding && understanding.totalVotes > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography sx={{ fontSize: 13, color: '#059669' }}>
                    Fully {understanding.understoodFully}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#d97706' }}>
                    Partial {understanding.partiallyUnderstood}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#dc2626' }}>
                    Did not {understanding.didNotUnderstand}
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ fontSize: 14, color: '#94a3b8' }}>No grasp votes</Typography>
              )}
            </Box>

            <Box sx={cardSx}>
              <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <StarRoundedIcon sx={{ color: '#f59e0b' }} />
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>Post Quiz</Typography>
              </Box>
              {avgPreQuiz == null && avgPostQuiz == null ? (
                <Typography sx={{ fontSize: 14, color: '#94a3b8' }}>No quiz data</Typography>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: 11, color: '#64748b' }}>Pre</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#059669', lineHeight: 1.1 }}>
                      {avgPreQuiz != null ? `${avgPreQuiz}%` : '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 11, color: '#64748b' }}>Post</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#7c3aed', lineHeight: 1.1 }}>
                      {avgPostQuiz != null ? `${avgPostQuiz}%` : '—'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Box sx={cardSx}>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <VisibilityOutlinedIcon sx={{ color: '#2563eb' }} />
                {attentionDelta != null ? (
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: attentionDelta < 0 ? '#dc2626' : '#059669',
                    }}
                  >
                    {attentionDelta > 0 ? '+' : ''}
                    {attentionDelta}% vs Avg
                  </Typography>
                ) : null}
              </Box>
              <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>
                {avgAttention != null ? `${avgAttention}%` : '—'}
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 14, color: '#64748b' }}>Engagement (focus)</Typography>
              {engagementOutreach ? (
                <Typography sx={{ mt: 1, fontSize: 12, color: '#64748b' }}>
                  Doubts {engagementOutreach.doubtsCount} · Chat{' '}
                  {engagementOutreach.chatOutreachCount}
                </Typography>
              ) : null}
            </Box>

            <Box
              component="button"
              type="button"
              onClick={() => {
                if (feedback && feedback.ratingCount > 0) setFeedbackOpen(true);
              }}
              disabled={!feedback || feedback.ratingCount <= 0}
              sx={{
                ...cardSx,
                textAlign: 'left',
                font: 'inherit',
                cursor:
                  feedback && feedback.ratingCount > 0 ? 'pointer' : 'default',
                transition: 'box-shadow 120ms ease, border-color 120ms ease',
                '&:hover':
                  feedback && feedback.ratingCount > 0
                    ? {
                        borderColor: '#93c5fd',
                        boxShadow: '0 8px 24px rgba(37, 99, 235, 0.12)',
                      }
                    : undefined,
                '&:disabled': { opacity: 1 },
              }}
            >
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <StarRoundedIcon sx={{ color: '#f59e0b' }} />
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>Feedback</Typography>
              </Box>
              {feedback && feedback.ratingCount > 0 && feedback.avgRating != null ? (
                <>
                  <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>
                    {feedback.avgRating}/5
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: '#64748b' }}>
                    {feedback.ratingCount} rating{feedback.ratingCount === 1 ? '' : 's'}
                  </Typography>
                  <Typography sx={{ mt: 1, fontSize: 12, fontWeight: 700, color: '#2563eb' }}>
                    View all feedback
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 14, color: '#94a3b8' }}>
                  No end-of-session ratings yet
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              mb: 3,
              display: 'grid',
              gap: 2.5,
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            }}
          >
            {topicTimes.length === 0 ? (
              <EmptyCard
                title="Topic Time: Planned vs Actual"
                message="No topic timing data for this session."
              />
            ) : (
              <Box sx={{ ...cardSx, p: 3 }}>
                <Typography sx={{ mb: 2.5, fontWeight: 800, fontSize: 17, color: '#0f172a' }}>
                  Topic Time: Planned vs Actual
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                  {topicTimes.map((t) => {
                    const planned = Math.max(0, Math.round(Number(t.plannedMinutes) || 0));
                    const actual = Math.max(0, Math.round(Number(t.actualMinutes) || 0));
                    const delta = actual - planned;
                    const hasTaken = actual > 0;
                    const deltaColor =
                      delta > 0 ? '#c2410c' : delta < 0 ? '#16a34a' : '#64748b';
                    return (
                      <Box key={t.title}>
                        <Box
                          sx={{
                            mb: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            gap: 1.5,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 15,
                              fontWeight: 700,
                              color: '#0f172a',
                              minWidth: 0,
                            }}
                          >
                            {t.title}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: '#64748b',
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                            }}
                          >
                            {planned}m / {actual}m{' '}
                            <Box component="span" sx={{ color: deltaColor, fontWeight: 600 }}>
                              ({delta > 0 ? '+' : ''}
                              {delta})
                            </Box>
                          </Typography>
                        </Box>
                        {/* Solid status bar: gray = not taken, sky = has actual time */}
                        <Box
                          sx={{
                            height: 10,
                            borderRadius: 999,
                            backgroundColor: hasTaken ? '#38bdf8' : '#94a3b8',
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {sortedConfusion.length === 0 ? (
              <EmptyCard
                title="Confusion by topic"
                message="No confusion data for this session."
              />
            ) : (
              <Box sx={{ ...cardSx, p: 3 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>Confusion by topic</Typography>
                <Typography sx={{ mb: 2, mt: 0.5, fontSize: 13, color: '#64748b' }}>
                  Sorted highest confusion first
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {sortedConfusion.map((item, index) => {
                    const isHighest = index === 0;
                    const barPct = (item.confusionPercent / maxTopicConfusion) * 100;
                    return (
                      <Box key={item.topicTitle}>
                        <Box sx={{ mb: 0.75, display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontWeight: 700, color: isHighest ? '#991b1b' : '#0f172a' }}>
                            {item.topicTitle}
                          </Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: 14, color: isHighest ? '#dc2626' : '#475569' }}>
                            {item.confusionPercent}% confused
                          </Typography>
                        </Box>
                        <Box sx={{ height: 12, borderRadius: 999, backgroundColor: '#fee2e2', overflow: 'hidden' }}>
                          <Box
                            sx={{
                              height: '100%',
                              borderRadius: 999,
                              backgroundColor: isHighest ? '#ef4444' : '#fca5a5',
                              width: `${barPct}%`,
                            }}
                          />
                        </Box>
                        {item.sessionMinute != null && (
                          <Typography sx={{ mt: 0.5, fontSize: 12, color: '#94a3b8' }}>
                            Peak around {item.sessionMinute}m into session
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
                {peakTopic && (
                  <Box
                    sx={{
                      mt: 2,
                      borderRadius: '8px',
                      backgroundColor: '#fef2f2',
                      color: '#991b1b',
                      px: 1.5,
                      py: 1,
                      fontSize: 12,
                    }}
                  >
                    Highest confusion — {peakTopic.topicTitle}
                    {peakTopic.sessionMinute != null ? ` (around ${peakTopic.sessionMinute}m)` : ''}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ ...cardSx, p: 3, mb: 3 }}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>
                Students Needing Follow-up
              </Typography>
              {followUpStudents.length > 0 ? (
                <Box
                  sx={{
                    borderRadius: '999px',
                    backgroundColor: '#fee2e2',
                    color: '#b91c1c',
                    px: 1.25,
                    py: 0.5,
                    fontSize: 12,
                    fontWeight: 800,
                  }}
                >
                  {followUpStudents.length} Critical
                </Box>
              ) : null}
            </Box>
            {!quizDataAvailable ? (
              <Typography sx={{ fontSize: 14, color: '#64748b' }}>
                No quiz scores available to identify follow-up students.
              </Typography>
            ) : filteredFollowUp.length === 0 ? (
              <Typography sx={{ fontSize: 14, color: '#64748b' }}>
                No students match the current filter.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {filteredFollowUp.slice(0, 3).map((s) => (
                  <Box
                    key={s.id}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      borderRadius: '12px',
                      border: '1px solid #f1f5f9',
                      p: 1.5,
                    }}
                  >
                    <StudentAvatar src={s.avatarUrl} name={s.name} />
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>{s.name}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748b' }}>{s.flags.join(' · ')}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            {quizDataAvailable && studentFilter !== 'best' && needHelpCount > 0 && (
              <Button
                onClick={() => {
                  setStudentFilter('need-help');
                  setTab('quiz-score');
                }}
                sx={{
                  mt: 1.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#2563eb',
                  px: 0,
                  '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                }}
              >
                View All {needHelpCount} High-Risk Students
              </Button>
            )}
          </Box>

          {quizPushes.length === 0 ? (
            <EmptyCard title="Pre/Post Quiz Pushes" message="No quiz push data for this session." />
          ) : (
            <Box sx={{ ...cardSx, mb: 2 }}>
              <Typography sx={{ mb: 2, fontWeight: 800, fontSize: 16, color: '#0f172a' }}>
                Pre/Post Quiz Pushes
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {quizPushes.map((q) => (
                  <Box
                    key={q.id || q.label}
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      borderRadius: '12px',
                      border: '1px solid #f1f5f9',
                      px: 1.5,
                      py: 1.25,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{q.label}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                        {q.submitted} students submitted
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          borderRadius: '999px',
                          px: 1,
                          py: 0.25,
                          fontSize: 10,
                          fontWeight: 800,
                          backgroundColor: '#f1f5f9',
                          color: '#475569',
                        }}
                      >
                        {q.status}
                      </Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#059669' }}>
                        {q.avgScore}% avg
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}

      {tab === 'attendee' && (
        <>
          <Box sx={{ mb: 2 }}>
            <StudentFilterToggle
              value={studentFilter}
              onChange={setStudentFilter}
              bestCount={bestCount}
              needHelpCount={needHelpCount}
              quizDataAvailable={quizDataAvailable}
            />
          </Box>
          <AttendeeTable rows={filteredAttendees} showQuiz={false} showMetrics />
        </>
      )}

      {tab === 'avg-attendee' && (
        <Box sx={{ ...cardSx, p: 4 }}>
          {totalStudents === 0 ? (
            <Typography sx={{ color: '#64748b' }}>No attendance data for this session.</Typography>
          ) : (
            <>
              <Typography sx={{ fontSize: 40, fontWeight: 800, color: '#2563eb' }}>
                {attendancePercent}%
              </Typography>
              <Typography sx={{ mt: 1, color: '#475569' }}>
                {attendanceCount} of {totalStudents} students attended ({attendanceCount}/{totalStudents}).
              </Typography>
            </>
          )}
        </Box>
      )}

      {tab === 'quiz-score' && (
        <>
          <Box sx={{ mb: 2 }}>
            <StudentFilterToggle
              value={studentFilter}
              onChange={setStudentFilter}
              bestCount={bestCount}
              needHelpCount={needHelpCount}
              quizDataAvailable={quizDataAvailable}
            />
          </Box>
          {!quizDataAvailable ? (
            <EmptyCard message="No quiz score data for this session." />
          ) : (
            <AttendeeTable rows={filteredAttendees} showQuiz showMetrics />
          )}
        </>
      )}
      </Box>

      <Dialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: '16px' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
            fontWeight: 800,
            fontSize: 18,
            color: '#0f172a',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <StarRoundedIcon sx={{ color: '#f59e0b' }} />
              Session feedback
              {feedback?.avgRating != null ? (
                <Typography
                  component="span"
                  sx={{ ml: 0.5, fontSize: 14, fontWeight: 700, color: '#64748b' }}
                >
                  · {feedback.avgRating}/5 · {feedback.ratingCount} rating
                  {feedback.ratingCount === 1 ? '' : 's'}
                </Typography>
              ) : null}
            </Box>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: 12,
                fontWeight: 600,
                color: '#94a3b8',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {sessionTitle}
              {sessionId ? ` · Session ID ${sessionId}` : ''}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={() => setFeedbackOpen(false)}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2.5, py: 2 }}>
          {!feedback || feedback.reviews.length === 0 ? (
            <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>
              No student feedback yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {feedback.reviews.map((r, idx) => {
                const submittedLabel = formatFeedbackSubmittedAt(r.submitted_at);
                return (
                  <Box
                    key={`${r.student_id || 'r'}-${idx}`}
                    sx={{
                      borderRadius: '12px',
                      border: '1px solid #f1f5f9',
                      backgroundColor: '#f8fafc',
                      px: 1.5,
                      py: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                        {r.student_name || r.student_id || 'Student'}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 800,
                          color: '#b45309',
                          flexShrink: 0,
                        }}
                      >
                        {r.rating}/5
                      </Typography>
                    </Box>
                    {submittedLabel ? (
                      <Typography sx={{ mt: 0.35, fontSize: 11, color: '#94a3b8' }}>
                        {submittedLabel}
                      </Typography>
                    ) : null}
                    {r.review ? (
                      <Typography
                        sx={{
                          mt: 0.75,
                          fontSize: 13,
                          color: '#475569',
                          lineHeight: 1.45,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {r.review}
                      </Typography>
                    ) : (
                      <Typography sx={{ mt: 0.75, fontSize: 12, color: '#94a3b8' }}>
                        No written review
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PostSessionTrainerReport;
